import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  XMarkIcon,
  TrophyIcon,
  PuzzlePieceIcon,
  CurrencyDollarIcon,
  CheckIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  NoSymbolIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';
import useTranslation from '../services/translationService';
import { useNotification } from '../components/NotificationContext';
import { formatCurrency } from '../utils/currency';
import { fetchBetLimits, saveBetLimit, saveBulkBetLimits } from '../services/limitsService';
import { fetchCustomSportsEvents, createCustomSportsEvent } from '../services/sportsCatalogService';
import {
  buildBetScopeOptions,
  buildLimitPayload,
  findUserChain,
  getAllSportsEvents,
  getNodeCurrencies,
  getTypeColor,
  getTypeIcon,
  getTypeLabel,
  isBlockingLimit,
  limitMatchesScope,
  sportsEventMatchesQuery,
  sportsEventToScope,
  toEventId
} from '../utils/limitScopes';

/**
 * Set a bet limit straight from something the admin is already looking at — a
 * casino game or a sports league on a bet — instead of going to the Limits page
 * and searching for the player and the scope by hand.
 *
 * The limit still lands in the same place: one record per currency on the
 * Limits table, attached to whichever username in the player's chain is picked
 * as the target.
 */
const LimitScopeModal = ({
  isOpen,
  onClose,
  bet,
  player,
  userTree,
  currency,
  onSaved,
  // Optional context from wherever the panel was opened. The limit history uses
  // all three: it knows which account already holds the limit that refused the
  // bet, and it knows the stake that would have gone through.
  notice,
  suggestion,
  defaultTargetUsername,
  preferredScopeKey
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();

  const [customEvents, setCustomEvents] = useState([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [selectedScopeKey, setSelectedScopeKey] = useState(null);
  const [manualScope, setManualScope] = useState(null);
  const [showEventPicker, setShowEventPicker] = useState(false);
  const [eventSearch, setEventSearch] = useState('');
  const [targetIndex, setTargetIndex] = useState(null);
  const [existingLimits, setExistingLimits] = useState([]);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [currencyRows, setCurrencyRows] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // ---------------------------------------------------------------------
  // Scope
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setIsLoadingCatalog(true);

    fetchCustomSportsEvents()
      .then((events) => {
        if (!cancelled) setCustomEvents(events || []);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCatalog(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const { options: scopeOptions, unmatchedLeague, matchConfidence } = useMemo(
    () => buildBetScopeOptions(bet, customEvents),
    [bet, customEvents]
  );

  const allScopeOptions = useMemo(
    () => (manualScope ? [manualScope, ...scopeOptions.filter((s) => s.key !== manualScope.key)] : scopeOptions),
    [manualScope, scopeOptions]
  );

  // Default to the narrowest scope, which is the one the bet came from — unless
  // the caller named one. A refusal names the scope that actually did the
  // refusing, so "Roulette (all variants)" starts selected when that is what
  // capped the bet, rather than the single variant it was placed on.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedScopeKey((current) => {
      if (current && allScopeOptions.some((scope) => scope.key === current)) return current;
      const preferred = allScopeOptions.find((scope) => scope.key === preferredScopeKey);
      return preferred?.key || allScopeOptions[0]?.key || null;
    });
  }, [isOpen, allScopeOptions, preferredScopeKey]);

  const selectedScope = useMemo(
    () => allScopeOptions.find((scope) => scope.key === selectedScopeKey) || null,
    [allScopeOptions, selectedScopeKey]
  );

  // ---------------------------------------------------------------------
  // Target (who the limit is attached to)
  // ---------------------------------------------------------------------

  const chain = useMemo(() => {
    const found = findUserChain(userTree, player?.username);
    if (found.length > 0) return found;
    if (!player?.username) return [];
    return [{ username: player.username, name: player.name || player.username, type: player.type || 'player' }];
  }, [userTree, player]);

  // Default to the player, unless the caller named the account it wants — the
  // history opens straight onto whoever owns the limit that did the refusing.
  useEffect(() => {
    if (!isOpen) return;
    setTargetIndex((current) => {
      if (current !== null) return current;
      const named = chain.findIndex((node) => node.username === defaultTargetUsername);
      return named >= 0 ? named : Math.max(chain.length - 1, 0);
    });
  }, [isOpen, chain, defaultTargetUsername]);

  const targetNode = chain[targetIndex ?? chain.length - 1] || null;
  const targetUsername = targetNode?.username || null;
  const isWiderThanPlayer = targetIndex !== null && targetIndex < chain.length - 1;

  // ---------------------------------------------------------------------
  // Existing limits on the target
  // ---------------------------------------------------------------------

  const loadLimits = useCallback(async () => {
    if (!targetUsername) return;

    setIsLoadingLimits(true);
    try {
      const result = await fetchBetLimits(targetUsername);
      setExistingLimits(result.success ? result.limits || [] : []);
    } catch (error) {
      console.error('Error loading limits for', targetUsername, error);
      setExistingLimits([]);
    } finally {
      setIsLoadingLimits(false);
    }
  }, [targetUsername]);

  useEffect(() => {
    if (!isOpen || !targetUsername) return;
    loadLimits();
  }, [isOpen, targetUsername, loadLimits]);

  const scopeLimits = useMemo(
    () => (selectedScope ? existingLimits.filter((limit) => limitMatchesScope(limit, selectedScope)) : []),
    [existingLimits, selectedScope]
  );

  // ---------------------------------------------------------------------
  // Currencies — the table holds one currency per limit, so covering several
  // means saving several records, each with its own amount.
  // ---------------------------------------------------------------------

  const availableCurrencies = useMemo(() => {
    const codes = new Set();
    if (currency) codes.add(currency);
    getNodeCurrencies(targetNode).forEach((code) => codes.add(code));
    getNodeCurrencies(chain[chain.length - 1]).forEach((code) => codes.add(code));
    scopeLimits.forEach((limit) => limit.currency && codes.add(limit.currency));
    if (codes.size === 0) ['USD', 'EUR', 'GBP'].forEach((code) => codes.add(code));
    return Array.from(codes);
  }, [currency, targetNode, chain, scopeLimits]);

  useEffect(() => {
    const rows = {};
    availableCurrencies.forEach((code) => {
      const existing = scopeLimits.find((limit) => limit.currency === code);
      rows[code] = {
        enabled: Boolean(existing) || code === currency,
        maxBet: existing?.maxBet != null ? String(existing.maxBet) : '',
        minBet: existing?.minBet ? String(existing.minBet) : ''
      };
    });
    setCurrencyRows(rows);
  }, [availableCurrencies, scopeLimits, currency]);

  const updateRow = (code, patch) =>
    setCurrencyRows((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));

  const enabledCurrencies = useMemo(
    () => Object.entries(currencyRows).filter(([, row]) => row.enabled),
    [currencyRows]
  );

  // ---------------------------------------------------------------------
  // Saving
  // ---------------------------------------------------------------------

  const handleSave = async () => {
    if (!selectedScope || !targetUsername) return;

    if (enabledCurrencies.length === 0) {
      showNotification('warning', t('common.warning'), t('limitScope.selectCurrency'));
      return;
    }

    const parsedRows = [];
    for (const [code, row] of enabledCurrencies) {
      const maxBet = parseFloat(row.maxBet);
      const minBet = row.minBet ? parseFloat(row.minBet) : 0;

      // 0 is allowed and means "blocked": the scope is closed and no bet is taken.
      if (isNaN(maxBet) || maxBet < 0) {
        showNotification('warning', t('common.warning'), `${code}: ${t('limits.invalidAmount')}`);
        return;
      }
      if (isNaN(minBet) || minBet < 0) {
        showNotification('warning', t('common.warning'), `${code}: ${t('limits.invalidAmount')}`);
        return;
      }
      if (maxBet === 0 && minBet > 0) {
        showNotification('warning', t('common.warning'), `${code}: ${t('limits.blockedNeedsNoMin')}`);
        return;
      }
      // A min above the max stores a limit that rejects every bet in the scope
      if (minBet > maxBet) {
        showNotification('warning', t('common.warning'), `${code}: ${t('limits.minAboveMax')}`);
        return;
      }

      parsedRows.push({ code, maxBet, minBet });
    }

    setIsSaving(true);

    try {
      let scope = selectedScope;

      // A league captured off a bet has to exist in the shared catalog before a
      // limit can point at it, otherwise the Limits page and the AI assistant
      // show a bare slug instead of the league.
      if (scope.pendingCatalogEvent) {
        const result = await createCustomSportsEvent(scope.pendingCatalogEvent);
        if (!result.success) {
          showNotification('error', t('common.error'), result.error || t('limitScope.errorCreatingEvent'));
          return;
        }

        const expectedId = toEventId(scope.pendingCatalogEvent.name);
        const created = (result.events || []).find((event) => event.id === expectedId);
        setCustomEvents(result.events || []);
        scope = {
          ...scope,
          sportsEventId: created?.id || expectedId,
          sportsEventName: created?.name || scope.pendingCatalogEvent.name,
          sportsEventCategory: created?.category || scope.pendingCatalogEvent.category,
          pendingCatalogEvent: undefined
        };
      }

      const limits = parsedRows.map(({ code, maxBet, minBet }) => {
        const payload = buildLimitPayload(scope, code, maxBet, minBet);
        const existing = scopeLimits.find((limit) => limit.currency === code);
        return existing ? { ...payload, id: existing.id } : payload;
      });

      const userType = targetNode?.type || 'player';
      const result =
        limits.length > 1
          ? await saveBulkBetLimits(targetUsername, limits, userType)
          : await saveBetLimit(targetUsername, limits[0], userType);

      if (!result.success) {
        showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
        return;
      }

      showNotification(
        'success',
        t('common.success'),
        t('limitScope.saved', {
          count: limits.length,
          scope: scope.label,
          target: targetUsername
        })
      );

      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error saving limit from bet:', error);
      showNotification('error', t('common.error'), error.message || t('limits.errorSavingLimit'));
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------------
  // Sports event picker
  // ---------------------------------------------------------------------

  const pickerEvents = useMemo(() => {
    const all = getAllSportsEvents(customEvents);
    const matched = all.filter((event) => sportsEventMatchesQuery(event, eventSearch));
    // The full catalog is long; an empty search stays on the first screenful,
    // and a search such as "league" has to show every match.
    return eventSearch.trim() ? matched : matched.slice(0, 40);
  }, [customEvents, eventSearch]);

  const handlePickEvent = (event) => {
    const scope = sportsEventToScope(event);
    setManualScope(scope);
    setSelectedScopeKey(scope.key);
    setShowEventPicker(false);
    setEventSearch('');
  };

  const handleClose = () => {
    setSelectedScopeKey(null);
    setManualScope(null);
    setTargetIndex(null);
    setExistingLimits([]);
    setShowEventPicker(false);
    setEventSearch('');
    onClose?.();
  };

  if (!isOpen) return null;

  const isSports = bet?.isSports;
  const title = isSports
    ? t('limitScope.titleSports')
    : t('limitScope.titleCasino');

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-slide-up sm:animate-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full sm:hidden" />

          <div className="mt-2 sm:mt-0 min-w-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <ShieldExclamationIcon className="w-5 h-5 text-amber-500 shrink-0" />
              <span className="truncate">{title}</span>
            </h2>
            {player?.username && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {t('limitScope.fromBetOf')} @{player.username}
              </p>
            )}
          </div>

          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation shrink-0"
          >
            <XMarkIcon className="w-6 h-6 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
          {notice && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-2.5">
              <InformationCircleIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-800 dark:text-blue-200 leading-snug">{notice}</p>
            </div>
          )}

          {/* 1. What gets limited */}
          <section>
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('limitScope.whatToLimit')}
              </label>
              {isSports && (
                <button
                  type="button"
                  onClick={() => setShowEventPicker(true)}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline touch-manipulation"
                >
                  {t('limitScope.chooseAnother')}
                </button>
              )}
            </div>

            {isLoadingCatalog && isSports ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-3">
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                {t('limitScope.loadingCatalog')}
              </div>
            ) : (
              <div className="space-y-2">
                {allScopeOptions.map((scope) => {
                  const isSelected = scope.key === selectedScopeKey;
                  return (
                    <button
                      key={scope.key}
                      type="button"
                      onClick={() => setSelectedScopeKey(scope.key)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all touch-manipulation flex items-start gap-3 ${
                        isSelected
                          ? isSports
                            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                            : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <span className="shrink-0 mt-0.5">
                        {scope.icon ? (
                          <span className="text-xl leading-none">{scope.icon}</span>
                        ) : isSports ? (
                          <TrophyIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <PuzzlePieceIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-gray-900 dark:text-gray-100 truncate">
                          {scope.label}
                        </span>
                        {scope.description && (
                          <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                            {scope.description}
                          </span>
                        )}
                      </span>
                      {isSelected && <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}

            {unmatchedLeague && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2.5">
                <InformationCircleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  {t('limitScope.newLeagueNotice', { league: unmatchedLeague })}
                </p>
              </div>
            )}

            {matchConfidence === 'partial' && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-2.5">
                <InformationCircleIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  {t('limitScope.partialMatchNotice')}
                </p>
              </div>
            )}
          </section>

          {/* 2. Who it applies to */}
          <section>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('limitScope.applyTo')}
            </label>

            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {chain.map((node, index) => {
                const Icon = getTypeIcon(node.type);
                const isSelected = index === (targetIndex ?? chain.length - 1);
                return (
                  <button
                    key={`${node.username}-${index}`}
                    type="button"
                    onClick={() => setTargetIndex(index)}
                    className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all touch-manipulation ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <span className={`p-1 rounded-md ${getTypeColor(node.type)}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="text-left">
                      <span className="block text-xs font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {node.name || node.username}
                      </span>
                      <span className="block text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {getTypeLabel(node.type)}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            {isWiderThanPlayer && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-2.5">
                <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  {t('limitScope.widerThanPlayerWarning', {
                    target: targetNode?.name || targetUsername,
                    player: player?.username
                  })}
                </p>
              </div>
            )}

            {!isWiderThanPlayer && chain.length > 1 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <ArrowUpIcon className="w-3 h-3" />
                {t('limitScope.goUpHint')}
              </p>
            )}
          </section>

          {/* 3. Currencies and amounts */}
          <section>
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('limitScope.currenciesAndAmounts')}
              </label>
              {isLoadingLimits && <ArrowPathIcon className="w-4 h-4 animate-spin text-gray-400" />}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('limitScope.currencyHint')}
            </p>

            {/* One tap for the amount the caller already worked out — from the
                history that is the stake that was refused, so accepting it lets
                that exact bet through. */}
            {suggestion?.currency && (
              <button
                type="button"
                onClick={() =>
                  updateRow(suggestion.currency, {
                    enabled: true,
                    maxBet: String(suggestion.maxBet)
                  })
                }
                className="w-full mb-3 inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 border-dashed border-primary-400 dark:border-primary-600 text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors touch-manipulation"
              >
                <ArrowUpIcon className="w-4 h-4" />
                {suggestion.label ||
                  `${t('limitScope.setMaxTo', {}, 'Set the maximum to')} ${formatCurrency(
                    Number(suggestion.maxBet),
                    suggestion.currency,
                    { compact: false }
                  )}`}
              </button>
            )}

            <div className="space-y-2">
              {availableCurrencies.map((code) => {
                const row = currencyRows[code] || { enabled: false, maxBet: '', minBet: '' };
                const existing = scopeLimits.find((limit) => limit.currency === code);

                return (
                  <div
                    key={code}
                    className={`rounded-lg border transition-colors ${
                      row.enabled
                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/10'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <label className="flex items-center gap-3 p-3 cursor-pointer touch-manipulation">
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(e) => updateRow(code, { enabled: e.target.checked })}
                        className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{code}</span>
                      {existing && (
                        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                          {t('limitScope.currentMax')}:{' '}
                          {isBlockingLimit(existing)
                            ? t('limits.blocked')
                            : formatCurrency(existing.maxBet, code)}
                        </span>
                      )}
                    </label>

                    {row.enabled && (
                      <div className="grid grid-cols-2 gap-2 px-3 pb-3">
                        <div>
                          <span className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {t('limits.maxBet')} *
                          </span>
                          <div className="relative">
                            <CurrencyDollarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="number"
                              inputMode="decimal"
                              value={row.maxBet}
                              onChange={(e) => updateRow(code, { maxBet: e.target.value })}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="w-full pl-8 pr-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>
                        <div>
                          <span className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
                            {t('limitScope.minBetOptional')}
                          </span>
                          <div className="relative">
                            <CurrencyDollarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="number"
                              inputMode="decimal"
                              value={row.minBet}
                              onChange={(e) => updateRow(code, { minBet: e.target.value })}
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              className="w-full pl-8 pr-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>

                        {parseFloat(row.maxBet) === 0 && (
                          <p className="col-span-2 flex items-center gap-1 text-[11px] font-medium text-red-600 dark:text-red-400">
                            <NoSymbolIcon className="w-3.5 h-3.5" />
                            {t('limitScope.blockedNotice', { currency: code })}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* 4. What already exists on this target */}
          {existingLimits.length > 0 && (
            <section>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('limitScope.otherLimitsOn', { target: targetNode?.name || targetUsername })}
              </label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {existingLimits.map((limit) => (
                  <div
                    key={limit.id}
                    className={`flex items-center justify-between gap-2 text-xs px-2.5 py-1.5 rounded-md ${
                      limitMatchesScope(limit, selectedScope)
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200'
                        : 'bg-gray-50 dark:bg-gray-900/40 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    <span className="truncate font-mono">{limit.identifier}</span>
                    <span
                      className={`shrink-0 font-medium ${
                        isBlockingLimit(limit) ? 'text-red-600 dark:text-red-400' : ''
                      }`}
                    >
                      {limit.currency}{' '}
                      {isBlockingLimit(limit)
                        ? t('limits.blocked')
                        : formatCurrency(limit.maxBet, limit.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 shrink-0 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={handleSave}
            disabled={isSaving || !selectedScope || !targetUsername || enabledCurrencies.length === 0}
            className="flex-1 px-4 py-3 sm:py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600 transition-colors inline-flex items-center justify-center gap-2 font-medium shadow-sm touch-manipulation"
          >
            {isSaving ? (
              <>
                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              <>
                <CheckIcon className="w-5 h-5" />
                {enabledCurrencies.length > 1
                  ? t('limitScope.saveMulti', { count: enabledCurrencies.length })
                  : t('common.save')}
              </>
            )}
          </button>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="flex-1 px-4 py-3 sm:py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 active:bg-gray-400 dark:active:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium touch-manipulation"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>

      {/* Sports event picker */}
      {showEventPicker && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={(e) => {
            // Without this the click reaches the panel overlay behind it and closes both
            e.stopPropagation();
            setShowEventPicker(false);
          }}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                {t('limits.selectSportsEvent')}
              </h3>
              <button
                onClick={() => setShowEventPicker(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-3 shrink-0">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder={t('limits.searchSportsEvents')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
              {pickerEvents.map((event) => (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => handlePickEvent(event)}
                  className="w-full flex items-center gap-2 p-2.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-left touch-manipulation"
                >
                  <span className="text-lg leading-none shrink-0">{event.icon || '🏆'}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {event.name}
                    </span>
                    <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                      {event.category}
                    </span>
                  </span>
                </button>
              ))}
              {pickerEvents.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                  {t('limits.noEventsFound')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LimitScopeModal;
